import { DataGridColumn } from '@/components/DataGrid/DataGridColumn.class';
import { DataGridColumnType } from '@/components/DataGrid/DataGridColumnType.class';
import { IBox } from '@/typing/api/box.interface';
import { Trip } from '@/typing/api/tripNew.class';
import { getState } from '@/utils/vueInstance';
import $dayjs from '@/utils/dayjs';
import timeValidator from '@/utils/validators/timeFormatValidator';
import dateValidator from '@/utils/validators/dateValidator';
import {
    boxValidator,
    getTripTimes,
} from '@/utils/validators/miscTripValidators';
import numberValidator from '@/utils/validators/numberValidator';
import {
    dateFiller,
    timeFiller,
    numberFiller,
    datePickerFiller,
} from './gridFilters';
import { statusIconNameFn, statusIconColorFn, iconTitleFn } from './statusses';
import isSelectionOptionValidator from '@/utils/validators/isSelectionOptionValidator';
import { DataGridErrorType } from '@/typing/enums/dataGridErrorType.enum';

function getTripIndex(trips: Trip[], t: Trip): number {
    return trips.findIndex((trip: Trip) => t.number === trip.number);
}

function getPreviousTrip(trips: Trip[], t: Trip): Trip | null {
    const tripIndex = getTripIndex(trips, t);
    return tripIndex > 0 ? trips[tripIndex - 1] : null;
}

export function getBoxList(type: 'Box' | 'DumpingBox'): IBox[] {
    return (
        getState().project.boxList?.filter((b: IBox) => b.boxType === type) ??
        []
    );
}

export function getGridColumns(shipMaxVolume: number): DataGridColumn[] {
    const dumpingIsRequired = shipMaxVolume > 0;

    const smallMaxSize = dumpingIsRequired ? '80px' : '120px';
    const timeDateSize = dumpingIsRequired ? '90px' : '130px';
    const mediumMaxSize = dumpingIsRequired ? '130px' : '170px';

    const columnArr: DataGridColumn[] = [];

    columnArr.push(
        new DataGridColumn({
            header: 'Reisnr',
            valueKey: 'number',
            maxWidthCSS: smallMaxSize,
            isEditable: false,
        })
    );
    columnArr.push(
        new DataGridColumn({
            header: '',
            valueKey: '',
            isIconColumn: true,
            iconNameFn: statusIconNameFn,
            iconColorHexFn: statusIconColorFn,
            iconTitleFn,
            maxWidthCSS: '75px',
        })
    );
    columnArr.push(
        new DataGridColumn({
            header: 'Datum',
            valueKey: 'date',
            maxWidthCSS: '85px',
            minWidthCSS: '85px',
            isEditable: true,
            validatorFunc: (val: any, item: Trip, trips: Trip[]) => {
                const numberValidatorResult = dateValidator(val);
                if (numberValidatorResult.hasError) {
                    return numberValidatorResult as any;
                }

                const previousTrip = getPreviousTrip(trips, item);
                if (!previousTrip) {
                    return {
                        hasError: false,
                    };
                }

                const previousDate = $dayjs(previousTrip.date, 'DD/MM/YYYY');
                const tripDate = $dayjs(item.date, 'DD/MM/YYYY');

                if (tripDate < previousDate) {
                    return {
                        hasError: true,
                        message: 'Datum kan niet voor de vorige reis liggen.',
                    };
                }

                return {
                    hasError: false,
                };
            },
            valueWhenBlurOnEmptyFunc: dateFiller,
            required: true,
        })
    );
    columnArr.push(
        new DataGridColumn({
            header: 'Start Cyclus',
            valueKey: 'startTime',
            additionalInputType: DataGridColumnType.TimeDate,
            dateValueKey: 'startTimeDate',
            validatorFunc: (
                val: any,
                item: Trip,
                trips: Trip[],
                column: DataGridColumn,
                columns: DataGridColumn[]
            ) => {
                const timeValidatorResult = timeValidator(
                    val,
                    item,
                    column,
                    columns
                );
                if (timeValidatorResult.hasError) {
                    return timeValidatorResult as any;
                }

                const previousTrip: any = getPreviousTrip(trips, item);
                if (!previousTrip) {
                    return {
                        hasError: false,
                    };
                }

                const previousTripTimes = getTripTimes(previousTrip, true);
                let toCheckTime = null;

                const endOfTripProperty =
                    dumpingIsRequired || previousTrip.endTimeDumping
                        ? 'endTimeDumping'
                        : 'endTimeDredging';

                if (
                    previousTripTimes.endTimeDelay &&
                    previousTripTimes.endTimeDelay >
                        previousTripTimes[endOfTripProperty]
                ) {
                    toCheckTime = previousTripTimes.endTimeDelay;
                } else {
                    toCheckTime = previousTripTimes[endOfTripProperty];
                }

                const start = $dayjs(
                    `${item.startTimeDate} ${item.startTime}`,
                    'YYYY-MM-DD HH:mm'
                );

                if (start < toCheckTime) {
                    return {
                        hasError: true,
                        message:
                            'kan niet voor het einde van de vorige reis liggen.',
                    };
                }

                return {
                    hasError: false,
                };
            },
            valueWhenBlurOnEmptyFunc: timeFiller,
            dateUndefined: datePickerFiller,
            maxWidthCSS: smallMaxSize,
            minWidthCSS: timeDateSize,
            required: true,
        })
    );
    columnArr.push(
        new DataGridColumn({
            header: 'Vaknr',
            valueKey: 'boxNumber',
            additionalInputType: DataGridColumnType.Select,
            selectOptions: getBoxList('Box'),
            maxWidthCSS: smallMaxSize,
            required: true,
            validatorFunc: (val: any, item: Trip) =>
                boxValidator(val, getBoxList('Box'), item),
            selectOptionLabelFn: (item: IBox) =>
                `${item.boxNumber} - ${item.boxName}`,
            selectOptionValueFn: (item: IBox) => item.boxNumber,
            useSelectPossibilitiesWhenUndefined: true,
            autoComplete: true,
        })
    );
    columnArr.push(
        new DataGridColumn({
            header: 'Vak naam',
            valueKey: 'boxName',
            additionalInputType: DataGridColumnType.Select,
            selectOptions: getBoxList('Box'),
            required: true,
            validatorFunc: (val: any, item: Trip) =>
                boxValidator(val, getBoxList('Box'), item),
            selectOptionLabelFn: (item: IBox) =>
                `${item.boxNumber} - ${item.boxName}`,
            selectOptionValueFn: (item: IBox) => item.boxName,
            useSelectPossibilitiesWhenUndefined: true,
            autoComplete: true,
            maxWidthCSS: mediumMaxSize,
        })
    );
    columnArr.push(
        new DataGridColumn({
            header: 'Start baggeren',
            valueKey: 'startTimeDredging',
            additionalInputType: DataGridColumnType.TimeDate,
            dateValueKey: 'startTimeDredgingDate',
            validatorFunc: (
                val: string,
                item: Trip,
                items: Trip[],
                column: DataGridColumn,
                columns: DataGridColumn[]
            ) => {
                const timeValidatorResult = timeValidator(
                    val,
                    item,
                    column,
                    columns
                );
                if (timeValidatorResult.hasError) {
                    return timeValidatorResult as any;
                }

                return {
                    hasError: false,
                };
            },
            valueWhenBlurOnEmptyFunc: timeFiller,
            dateUndefined: datePickerFiller,
            maxWidthCSS: smallMaxSize,
            minWidthCSS: timeDateSize,
            required: true,
        })
    );
    columnArr.push(
        new DataGridColumn({
            header: 'Eind baggeren',
            valueKey: 'endTimeDredging',
            additionalInputType: DataGridColumnType.TimeDate,
            dateValueKey: 'endTimeDredgingDate',
            validatorFunc: (
                val: string,
                item: Trip,
                items: Trip[],
                column: DataGridColumn,
                columns: DataGridColumn[]
            ) => {
                const timeValidatorResult = timeValidator(
                    val,
                    item,
                    column,
                    columns
                );
                if (timeValidatorResult.hasError) {
                    return timeValidatorResult as any;
                }

                return {
                    hasError: false,
                };
            },
            valueWhenBlurOnEmptyFunc: timeFiller,
            dateUndefined: datePickerFiller,
            maxWidthCSS: smallMaxSize,
            minWidthCSS: timeDateSize,
            required: true,
        })
    );

    if (dumpingIsRequired) {
        columnArr.push(
            new DataGridColumn({
                header: 'm³',
                valueKey: 'volume',
                maxWidthCSS: smallMaxSize,
                validatorFunc: (val: any) => {
                    const numberValidatorResult = numberValidator(val);
                    if (numberValidatorResult.hasError) {
                        return numberValidatorResult as any;
                    }
                    const parsedNumber = Number(val);

                    if (parsedNumber > shipMaxVolume) {
                        return {
                            hasError: true,
                            message: `is meer dan de maximale inhoud van het schip (${shipMaxVolume} m³)`,
                        };
                    }

                    if (
                        shipMaxVolume > 0 &&
                        parsedNumber < shipMaxVolume * 0.5
                    ) {
                        return {
                            hasError: true,
                            message: `er is minder dan 50% van de maximale inhoud van het schip (${shipMaxVolume} m³) benut. Controleer of dit juist is`,
                            errorType: DataGridErrorType.Warning,
                        };
                    }

                    return {
                        hasError: false,
                    };
                },
                valueWhenBlurOnEmptyFunc: numberFiller,
                required: true,
            })
        );
        columnArr.push(
            new DataGridColumn({
                header: 'TDS',
                valueKey: 'tds',
                maxWidthCSS: smallMaxSize,
                valueWhenBlurOnEmptyFunc: numberFiller,
                validatorFunc: (val: any, trip: Trip) => {
                    const numberValidatorResult = numberValidator(val);
                    if (numberValidatorResult.hasError) {
                        return numberValidatorResult as any;
                    }

                    const parsedTDS = Number(val);

                    if (parsedTDS > shipMaxVolume) {
                        return {
                            hasError: true,
                            message: `is meer dan de maximale inhoud van het schip (${shipMaxVolume} m³)`,
                        };
                    }

                    if (
                        trip.volume &&
                        !numberValidator(trip.volume as any).hasError
                    ) {
                        const parsedVolume = Number(trip.volume);
                        if (parsedTDS > parsedVolume) {
                            return {
                                hasError: true,
                                message: `TDS (${parsedTDS}) kan niet meer zijn dan de ingevulde m³ (${parsedVolume})`,
                            };
                        }
                    }

                    return { hasError: false };
                },
                required: true,
            })
        );
    }
    columnArr.push(
        new DataGridColumn({
            header: 'Grondsoort',
            valueKey: 'soilType',
            additionalInputType: DataGridColumnType.Select,
            selectOptions: ['Slib', 'Zand'],
            required: true,
            maxWidthCSS: smallMaxSize,
        })
    );
    if (dumpingIsRequired) {
        columnArr.push(
            new DataGridColumn({
                header: 'Stortvak naam',
                valueKey: 'dumpBoxName',
                additionalInputType: DataGridColumnType.Select,
                selectOptions: getBoxList('DumpingBox'),
                required: dumpingIsRequired,
                selectOptionLabelFn: (item: IBox) =>
                    `${item.boxNumber} - ${item.boxName}`,
                selectOptionValueFn: (item: IBox) =>
                    `${item.boxNumber} - ${item.boxName}`,
                useSelectPossibilitiesWhenUndefined: true,
                maxWidthCSS: mediumMaxSize,
                validatorFunc: (val: any, t: Trip) => {
                    if (
                        !val &&
                        !dumpingIsRequired &&
                        !t.startTimeDumping &&
                        !t.endTimeDumping
                    ) {
                        return {
                            hasError: false,
                        };
                    }

                    if (
                        !dumpingIsRequired &&
                        !val &&
                        (t.startTimeDumping || t.endTimeDumping)
                    ) {
                        return {
                            hasError: true,
                            message:
                                'is een verplicht veld als andere stort velden ingevuld zijn',
                        };
                    }

                    return { hasError: false };
                },
            })
        );
        columnArr.push(
            new DataGridColumn({
                header: 'Start storten',
                additionalInputType: DataGridColumnType.TimeDate,
                dateValueKey: 'startTimeDumpingDate',
                valueKey: 'startTimeDumping',
                validatorFunc: (
                    val: any,
                    item: Trip,
                    trips: Trip[],
                    column: DataGridColumn,
                    columns: DataGridColumn[]
                ) => {
                    if (
                        !val &&
                        !dumpingIsRequired &&
                        !item.endTimeDumping &&
                        !item.dumpBoxName
                    ) {
                        return {
                            hasError: false,
                        };
                    }

                    if (
                        !dumpingIsRequired &&
                        !val &&
                        (item.endTimeDumping || item.dumpBoxName)
                    ) {
                        return {
                            hasError: true,
                            message:
                                'is een verplicht veld als andere stort velden ingevuld zijn',
                        };
                    }

                    if (!val && dumpingIsRequired) {
                        return { hasError: false };
                    }

                    const timeValidatorResult = timeValidator(
                        val,
                        item,
                        column,
                        columns
                    );
                    if (timeValidatorResult.hasError) {
                        return timeValidatorResult as any;
                    }

                    return {
                        hasError: false,
                    };
                },
                valueWhenBlurOnEmptyFunc: timeFiller,
                dateUndefined: datePickerFiller,
                maxWidthCSS: smallMaxSize,
                minWidthCSS: timeDateSize,
                required: dumpingIsRequired,
            })
        );
        columnArr.push(
            new DataGridColumn({
                header: 'Eind storten',
                valueKey: 'endTimeDumping',
                additionalInputType: DataGridColumnType.TimeDate,
                dateValueKey: 'endTimeDumpingDate',
                validatorFunc: (
                    val: any,
                    item: Trip,
                    trips: Trip[],
                    column: DataGridColumn,
                    columns: DataGridColumn[]
                ) => {
                    if (
                        !val &&
                        !dumpingIsRequired &&
                        !item.startTimeDumping &&
                        !item.dumpBoxName
                    ) {
                        return {
                            hasError: false,
                        };
                    }

                    if (
                        !dumpingIsRequired &&
                        !val &&
                        (item.startTimeDumping || item.dumpBoxName)
                    ) {
                        return {
                            hasError: true,
                            message:
                                'is een verplicht veld als andere stort velden ingevuld zijn',
                        };
                    }

                    if (!val && !item.startTimeDumping) {
                        return { hasError: false };
                    }

                    const timeValidatorResult = timeValidator(
                        val,
                        item,
                        column,
                        columns
                    );
                    if (timeValidatorResult.hasError) {
                        return timeValidatorResult as any;
                    }

                    return {
                        hasError: false,
                    };
                },
                valueWhenBlurOnEmptyFunc: timeFiller,
                dateUndefined: datePickerFiller,
                maxWidthCSS: smallMaxSize,
                minWidthCSS: timeDateSize,
                required: dumpingIsRequired,
            })
        );
    }
    columnArr.push(
        new DataGridColumn({
            header: 'Brandstof type',
            valueKey: 'fuel',
            additionalInputType: DataGridColumnType.Select,
            selectOptions: ['MDO', 'B30', 'B50', 'B70', 'B100', 'LNG', 'GTL'],
            showAllSelectOptions: true,
            required: true,
            maxWidthCSS: smallMaxSize,
        })
    );
    columnArr.push(
        new DataGridColumn({
            header: 'Verlet type',
            valueKey: 'delayType',
            additionalInputType: DataGridColumnType.Select,
            selectOptions: [
                'Bunkeren',
                'Verkeer',
                'Weer',
                'Technisch',
                'Rust',
                'Overig',
            ],
            showAllSelectOptions: true,
            maxWidthCSS: smallMaxSize,
            validatorFunc: isSelectionOptionValidator,
        })
    );
    columnArr.push(
        new DataGridColumn({
            header: 'Verlet start',
            valueKey: 'startTimeDelay',
            additionalInputType: DataGridColumnType.TimeDate,
            dateValueKey: 'startTimeDelayDate',
            valueWhenBlurOnEmptyFunc: timeFiller,
            dateUndefined: datePickerFiller,
            maxWidthCSS: smallMaxSize,
            minWidthCSS: timeDateSize,
            validatorFunc: (
                val: string,
                item: Trip,
                items: Trip[],
                column: DataGridColumn,
                columns: DataGridColumn[]
            ) => {
                if (!val && !item.delayType) {
                    return { hasError: false };
                }

                const timeValidatorResult = timeValidator(
                    val,
                    item,
                    column,
                    columns
                );
                if (timeValidatorResult.hasError) {
                    return timeValidatorResult as any;
                }

                return {
                    hasError: false,
                };
            },
        })
    );
    columnArr.push(
        new DataGridColumn({
            header: 'Verlet eind',
            valueKey: 'endTimeDelay',
            additionalInputType: DataGridColumnType.TimeDate,
            dateValueKey: 'endTimeDelayDate',
            valueWhenBlurOnEmptyFunc: timeFiller,
            dateUndefined: datePickerFiller,
            maxWidthCSS: smallMaxSize,
            minWidthCSS: timeDateSize,
            validatorFunc: (
                val: string,
                item: Trip,
                items: Trip[],
                column: DataGridColumn,
                columns: DataGridColumn[]
            ) => {
                if (!val && !item.delayType) {
                    return { hasError: false };
                }

                const timeValidatorResult = timeValidator(
                    val,
                    item,
                    column,
                    columns
                );
                if (timeValidatorResult.hasError) {
                    return timeValidatorResult as any;
                }

                return {
                    hasError: false,
                };
            },
        })
    );
    columnArr.push(
        new DataGridColumn({
            header: 'Opmerkingen',
            valueKey: 'remarks',
            valueWhenBlurOnEmptyFunc: () => '-',
        })
    );

    return columnArr;
}
